// Content for "Bhakti is self causative" topic

import type { TopicContent } from '../types';

const content: TopicContent = {
    primarySloka: {
        reference: 'Srimad Bhagavatam 1.2.6',
        sloka: `sa vai puṁsāṁ paro dharmo
yato bhaktir adhokṣaje |
ahaituky apratihatā
yayātmā suprasīdati ||`,
        translation:
            'The supreme dharma for all human beings (<strong>sa vai puṁsāṁ paro dharmo</strong>) (sādhana bhakti) is that by which prema-bhakti to the Lord arises (<strong>yato bhaktir adhokṣaje</strong>), which is not caused by anything other than itself (<strong>ahaituky</strong>), cannot be obstructed (<strong>apratihatā</strong>), and which satisfies the mind completely (<strong>yayātmā suprasīdati</strong>).',
    },
    blocks: [
        {
            type: 'text',
            content:
                'What is the final essence of all the scriptures? That essence is highest dharma for anyone who is a human being (puṁsām), <strong>meaning hearing and chanting about the Lord.</strong> It is said:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 6.3.22',
                sloka: `etāvān eva loke 'smin
puṁsāṁ dharmaḥ paraḥ smṛtaḥ
bhakti-yogo bhagavati
tan-nāma-grahaṇādibhiḥ`,
                translation:
                    'That is the only means for attainment of the final perfection of life. Therefore persons whose minds are fixed on the Lord engage in the intensive practice of devotional service.',
            },
        },
        {
            type: 'text',
            content:
                'By this statement, the word para along with dharma in the present verse can indicate only bhakti-yoga. Etavān eva indicates exclusion of other processes as para-dharma.<br /><br /><strong>The essence of scripture is bhakti-yoga by which prema-bhakti (yataḥ bhaktiḥ) appears. It arises without cause (ahaitukī).</strong><br /><br />Mixed bhakti is excluded in this definition (since it has cause).',
        },
        {
            type: 'dialogue',
            objection:
                '<em>But you are really evading the truth here (saying that prema is not caused by sādhana-bhakti).</em>',
            response:
                '<em>No. <strong>Dharma consisting of hearing and chanting about the Lord is called sādhana-bhakti, and in the mature state it is called prema. Both are called bhakti</strong>, for it is said bhaktyā sañjātayā bhaktyā bibhraty utpulakāṁ tanum: the devotee possesses a body with ecstatic symptoms by prema developed through sādhana-bhakti. (Srimad Bhagavatam 11.3.31).</em>',
        },
        {
            type: 'text',
            content:
                'In the verse being discussed, the former bhakti (paro dharmo) is the cause of the later type of bhakti (<em>yato bhaktir adhokṣaje</em>), just as an unripe mango is the cause of a ripe mango.',
        },
        {
            type: 'text',
            content:
                'Considering one the cause of the other because of the difference in taste is simply a conception for understanding the different strengths of bhakti, though sādhana-bhakti and prema are not actually different things.<br /><br />The various states of infancy, youth and adulthood in one person are actually not conditions of cause and results of that cause (since the person remains). On the other hand, when pots, cloth and cooked rice arise, the original names and forms of mud, thread and raw rice disappear.<br /><br /><strong>One cannot compare these examples to bhakti and prema.</strong>',
        },
        {
            type: 'heading',
            headingText: 'Reason #1: Devotee Association Cannot Be the Cause',
            content:
                "One also not say that the famous cause of bhakti is association of devotees, <strong>for association of devotees is part of bhakti. It is the second stage</strong>, as understood from the statement <em>ādau śraddhā tataḥ sādhu-saṅgo 'tha bjajana-kriyā.</em> (BRS 1.3.11)<br /><br />And later it will be said:",
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 1.2.16',
                sloka: `śuśrūṣoḥ śraddadhānasya
vāsudeva-kathā-ruciḥ
syān mahat-sevayā viprāḥ
puṇya-tīrtha-niṣevaṇāt`,
                translation:
                    'O twice-born sages, by serving those devotees who are completely freed from all vice, great service is done. By such service, one gains affinity for hearing the messages of Vāsudeva.',
            },
        },
        {
            type: 'heading',
            headingText: 'Reason #2: Pious Activities Cannot Be the Cause',
            content:
                'Such things as charity, vows, austerity, sacrifices, and selfless performance of duties are to some degree causes of bhakti in sattva-guṇa, practiced as an aṅga of jñāna. But they are not causes of pure bhakti, because it is said:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 11.12.9',
                sloka: `yaṁ na yogena sāṅkhyena
dāna-vrata-tapo-'dhvaraiḥ |
vyākhyā-svādhyāya-sannyāsaiḥ
prāpnuyād yatnavān api ||`,
                translation:
                    'Even though one engages with great endeavor in the mystic yoga system, philosophical speculation, charity, vows, penances, ritualistic sacrifices, teaching of Vedic mantras to others, personal study of the Vedas, or the renounced order of life, still one cannot achieve Me.',
            },
        },
        {
            type: 'heading',
            headingText: "Reason #3: Lord's Mercy Cannot Be the Absolute Cause",
            content:
                "Nor can it even be said that the mercy of the Lord is the cause of pure bhakti, for it is non-final cause, making one search out a further cause.<br /><br /><strong>One cannot say that the Lord's mercy is absolute, without further cause, for then it would mean the Lord is unjust and prejudiced in choosing to give mercy without reason to certain individuals and not to others.</strong>",
        },
        {
            type: 'heading',
            headingText: 'The Correct Understanding',
            content:
                "However if one says that the cause of bhakti is the mercy of the devotee, it is not so incorrect.<br /><br />Though the uttama-bhaktas do not make distinctions and are thus not prejudiced, one does see the madhyama-bhakta making distinctions between the Lord, the devotee, the innocent and the demon. Thus because the Lord is dependent on the devotee, <strong>the Lord's mercy follows after the mercy of the devotee</strong>.<br /><br />That is the correct conclusion.",
        },
        {
            type: 'text',
            content:
                "<strong>But then how is bhakti said to be without cause (ahaitukīm) in this verse?</strong><br /><br /><strong>Because the Lords mercy is included in the mercy of the devotee, and because that mercy is included in association with devotees, and because devotee association is an aṅga of bhakti, bhakti is said to be without cause (since an aṅga of bhakti causes bhakti).</strong><br /><br /><strong>Moreover the cause of devotee's mercy is but the bhakti present in the heart of the devotee, because without that bhakti in his heart there is no possibility of his mercy arising.</strong><br /><br /><strong>In all ways therefore, bhakti is the cause of bhakti. Therefore bhakti is said to be without cause.</strong>",
        },
        {
            type: 'text',
            content:
                "<strong>From the point of view of bhakti, the devotee, bhakti, the Lord, and his mercy are not separate items.</strong><br /><br />Even though bhakti appears by bhakti (thus being self-manifesting), it does not negate the fact that bhakti's self-manifesting nature comes from the Lord.",
        },
        {
            type: 'text',
            content: 'This bhakti cannot be prevented by anything (apratihatā). This is mentioned in the following verse:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 3.29.11',
                sloka: `mad-guṇa-śruti-mātreṇa
mayi sarva-guhāśaye
mano-gatir avicchinnā
yathā gaṅgāmbhaso 'mbudhau`,
                translation:
                    'Just as the water of the Ganges flows naturally down towards the ocean, such devotional ecstasy, uninterrupted by any material condition, flows towards the Supreme Lord.',
            },
        },
        {
            type: 'text',
            content:
                'Śrī Rūpa Gosvāmī has also said <em>sarvathā dhvaṁsa-rahitaṁ saty api dhvaṁsa-kāraṇe</em>: bhakti is without destruction, though it is the cause of destruction of obstacles. (Ujjvala-nīla-maṇi 14.63)<br /><br />Apratihatā can also mean that this prema-bhakti is not contaminated by jñāna or karma. By that bhakti (yayā) the mind (ātmā) becomes completely satisfied (suprasīdati).<br /><br /><strong>Because of the impossibility of the mind being satisfied with the presence of material desires, it is evident that this bhakti being discussed is without any material desires.</strong>',
        },
    ],
};

export default content;
